import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthContext } from '@/lib/AuthProvider';
import { useUserProfile } from '@/context/UserProfileContext';
import {
  AffiliateApplication,
  AffiliateDashboardData,
  CommissionRecord,
  PayoutRecord,
  SocialProfile,
  PaymentInfo
} from '@/types/affiliate';
import {
  getAffiliateApplication,
  getAffiliateDashboardData,
  getCommissionRecords,
  getPayoutRecords,
  getAffiliateStats,
  submitAffiliateApplication,
  recordConversion,
  getCommissionSourceAnalytics,
  getAffiliateDiscountCodes,
  getDiscountCodePerformance
} from '@/services/affiliateService';
import {
  handleReferralCode,
  getReferralCookie
} from '@/services/referralTrackingService';

// Define context type
interface AffiliateContextType {
  // Application state
  application: AffiliateApplication | null;
  dashboardData: AffiliateDashboardData | null;
  loading: boolean;
  error: string | null;
  
  // Application actions
  submitApplication: (
    profiles: SocialProfile[],
    website: string | undefined,
    paymentInfo: PaymentInfo
  ) => Promise<void>;
  refreshApplication: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  
  // Commission and payout data
  commissionRecords: CommissionRecord[];
  payoutRecords: PayoutRecord[];
  getCommissionsInDateRange: (startDate: Date, endDate: Date) => Promise<CommissionRecord[]>;
  getStatsInDateRange: (startDate: Date, endDate: Date) => Promise<{
    clicks: number;
    conversions: number;
    totalEarnings: number;
  }>;
  
  // Referral tracking
  handleReferralCode: (referralCode: string) => Promise<void>;
  recordOrderConversion: (orderId: string, orderValue: number) => Promise<void>;
  hasActiveReferral: () => boolean;
  
  // Commission analytics
  getCommissionSourceAnalytics: (startDate?: Date, endDate?: Date) => Promise<any>;
  getAffiliateDiscountCodes: () => Promise<any[]>;
  getDiscountCodePerformance: (startDate?: Date, endDate?: Date) => Promise<any[]>;
}

// Create context
const AffiliateContext = createContext<AffiliateContextType | undefined>(undefined);

// Provider component
export const AffiliateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [application, setApplication] = useState<AffiliateApplication | null>(null);
  const [dashboardData, setDashboardData] = useState<AffiliateDashboardData | null>(null);
  const [commissionRecords, setCommissionRecords] = useState<CommissionRecord[]>([]);
  const [payoutRecords, setPayoutRecords] = useState<PayoutRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useAuthContext();
  const { userProfile } = useUserProfile();

  // Load affiliate application when user changes
  useEffect(() => {
    if (user) {
      refreshApplication();
    } else {
      setApplication(null);
      setDashboardData(null);
      setCommissionRecords([]);
      setPayoutRecords([]);
    }
  }, [user]);

  // Load dashboard data when user is an approved affiliate
  useEffect(() => {
    if (user && userProfile?.isAffiliate && application?.status === 'approved') {
      refreshDashboard();
    }
  }, [user, userProfile?.isAffiliate, application?.status]);

  const refreshApplication = async (): Promise<void> => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const app = await getAffiliateApplication(user.uid);
      setApplication(app);
    } catch (err) {
      setError('Failed to load affiliate application');
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboard = async (): Promise<void> => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getAffiliateDashboardData(user.uid);
      setDashboardData(data);
      
      if (data) {
        setCommissionRecords(data.commissionRecords);
        setPayoutRecords(data.payoutRecords);
      }
    } catch (err) {
      setError('Failed to load affiliate dashboard');
    } finally {
      setLoading(false);
    }
  };

  const submitApplication = async (
    profiles: SocialProfile[],
    website: string | undefined,
    paymentInfo: PaymentInfo
  ): Promise<void> => {
    if (!user) {
      throw new Error('User must be authenticated to submit application');
    }

    try {
      setLoading(true);
      setError(null);
      
      await submitAffiliateApplication(user.uid, profiles, website, paymentInfo);
      
      // Refresh application data after submission
      await refreshApplication();
    } catch (err) {
      setError('Failed to submit affiliate application');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCommissionsInDateRange = async (
    startDate: Date,
    endDate: Date
  ): Promise<CommissionRecord[]> => {
    if (!application) {
      throw new Error('No affiliate application found');
    }

    try {
      return await getCommissionRecords(application.id, startDate, endDate);
    } catch (err) {
      throw new Error('Failed to get commission records');
    }
  };

  const getStatsInDateRange = async (
    startDate: Date,
    endDate: Date
  ): Promise<{ clicks: number; conversions: number; totalEarnings: number }> => {
    if (!application) {
      throw new Error('No affiliate application found');
    }

    try {
      return await getAffiliateStats(application.id, startDate, endDate);
    } catch (err) {
      throw new Error('Failed to get affiliate stats');
    }
  };

  const handleReferralCodeContext = async (referralCode: string): Promise<void> => {
    try {
      // Use the centralized referral handling function
      const result = await handleReferralCode(referralCode);
      
      if (!result.success) {
        // Silent error handling
      }
    } catch (err) {
      // Silent error handling
    }
  };

  const recordOrderConversion = async (orderId: string, orderValue: number): Promise<void> => {
    try {
      await recordConversion(orderId, orderValue);
      
      // Refresh dashboard data to show updated stats
      if (userProfile?.isAffiliate) {
        await refreshDashboard();
      }
    } catch (err) {
      // Silent error handling
    }
  };

  const hasActiveReferral = (): boolean => {
    const referralData = getReferralCookie();
    return referralData !== null;
  };

  const getCommissionSourceAnalyticsContext = async (startDate?: Date, endDate?: Date) => {
    if (!application) throw new Error('No affiliate application found');
    return await getCommissionSourceAnalytics(application.id, startDate, endDate);
  };

  const getAffiliateDiscountCodesContext = async () => {
    if (!application) throw new Error('No affiliate application found');
    return await getAffiliateDiscountCodes(application.id);
  };

  const getDiscountCodePerformanceContext = async (startDate?: Date, endDate?: Date) => {
    if (!application) throw new Error('No affiliate application found');
    return await getDiscountCodePerformance(application.id, startDate, endDate);
  };

  const contextValue: AffiliateContextType = {
    // State
    application,
    dashboardData,
    loading,
    error,
    commissionRecords,
    payoutRecords,
    
    // Actions
    submitApplication,
    refreshApplication,
    refreshDashboard,
    getCommissionsInDateRange,
    getStatsInDateRange,
    
    // Referral tracking
    handleReferralCode: handleReferralCodeContext,
    recordOrderConversion,
    hasActiveReferral,
    
    // Commission analytics
    getCommissionSourceAnalytics: getCommissionSourceAnalyticsContext,
    getAffiliateDiscountCodes: getAffiliateDiscountCodesContext,
    getDiscountCodePerformance: getDiscountCodePerformanceContext
  };

  return (
    <AffiliateContext.Provider value={contextValue}>
      {children}
    </AffiliateContext.Provider>
  );
};

// Hook to use affiliate context
export const useAffiliate = (): AffiliateContextType => {
  const context = useContext(AffiliateContext);
  if (context === undefined) {
    throw new Error('useAffiliate must be used within an AffiliateProvider');
  }
  return context;
};